/**
 * The ONE order in which a camera's missing setup is surfaced to the user.
 *
 * Read by the per-tile chip (`deriveChipState` / `deriveMissingBuckets` in
 * `room/components/video/sources/item/chip-state.ts`) and by the multi-add
 * notification router (`evaluateDesiredFires` in
 * `notifications/hooks/multi-add-bucket-resolver.ts`), so the chip verb, the
 * toast and the combined notification's deep-link always agree on which tab
 * the user should open next.
 *
 * Driver precedes Control because the driver decides the control protocol: a
 * camera discovered as a generic model (no driver) defaults to ONVIF control,
 * so asking for control credentials before a driver is chosen sends the user
 * to a tab that cannot succeed (QA finding 8, AIDA PTZ-NDI-X12). Control is
 * last and only surfaced when its transport actually failed.
 *
 * Video stays ahead of Driver: a video failure blocks the preview outright and
 * has its own tab, and the existing ITB report routing (video + model ->
 * Video) is unchanged.
 *
 * Dependency-free (type import only) so the resolver keeps its no-runtime-
 * coupling contract. Per `think-in-systems` (ai.knowledge/global): one
 * ordering, many callers. Per `reference-rules-in-comments`
 * (ai.knowledge/global).
 */
import type { MultiAddSkipReason } from '#~/notifications/types.js';

/** Missing-setup buckets the chip cascade can report, in the order to fix them. */
export const SETUP_BUCKET_ORDER = [
  'auth',
  'video-failed',
  'missing-model',
  'control-failed'
] as const satisfies readonly MultiAddSkipReason[];

/** Rank of a bucket in `SETUP_BUCKET_ORDER`; unknown buckets sort last. */
const rank = (bucket: MultiAddSkipReason): number => {
  const index = (SETUP_BUCKET_ORDER as readonly MultiAddSkipReason[]).indexOf(bucket);
  return index === -1 ? SETUP_BUCKET_ORDER.length : index;
};

/** Copy of `buckets` sorted into setup order (stable for unknown buckets). */
export const sortBySetupOrder = <T extends MultiAddSkipReason>(buckets: readonly T[]): T[] =>
  [...buckets].sort((a, b) => rank(a) - rank(b));

/** The bucket the user should fix first, or `undefined` when nothing is missing. */
export const firstInSetupOrder = <T extends MultiAddSkipReason>(
  buckets: readonly T[]
): T | undefined => sortBySetupOrder(buckets)[0];
